// src/pages/trading-portal/AIAnalysisEngine/SignalGenerationPage.js
// Signal Generation - tabbed interface for active signals, history, factor scores, and conflicts

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Grid,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
    Skeleton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    SignalCellularAlt as SignalCellularAltIcon,
    PlayArrow as PlayArrowIcon,
    History as HistoryIcon,
    Score as ScoreIcon,
    Gavel as GavelIcon,
    MergeType as MergeTypeIcon,
    Timer as TimerIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    SignalDirection,
    SignalDirectionLabels,
    SignalDirectionColors,
    SignalStrength,
    SignalStrengthLabels,
    SignalStrengthColors,
    AAESignalStatus,
    AAESignalStatusLabels,
    AAESignalStatusColors,
    FactorType,
    FactorTypeLabels,
    FactorTypeColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const SignalGenerationPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);

    // Tab
    const [activeTab, setActiveTab] = useState(0);

    // Global state
    const [error, setError] = useState(null);

    // ========== Tab 0: Active Signals ==========
    const [activeSignals, setActiveSignals] = useState([]);
    const [activeLoading, setActiveLoading] = useState(true);
    const [activePage, setActivePage] = useState(0);
    const [activeRowsPerPage, setActiveRowsPerPage] = useState(10);
    const [activeTotalCount, setActiveTotalCount] = useState(0);
    const [generatingSignal, setGeneratingSignal] = useState(false);
    const [generatingMultiFactor, setGeneratingMultiFactor] = useState(false);

    // ========== Tab 1: Signal History ==========
    const [signalHistory, setSignalHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
    const [historyTotalCount, setHistoryTotalCount] = useState(0);
    const [historyFilterDirection, setHistoryFilterDirection] = useState('');
    const [historyFilterStatus, setHistoryFilterStatus] = useState('');

    // ========== Tab 2: Factor Scores ==========
    const [factorScores, setFactorScores] = useState([]);
    const [factorsLoading, setFactorsLoading] = useState(false);
    const [factorsPage, setFactorsPage] = useState(0);
    const [factorsRowsPerPage, setFactorsRowsPerPage] = useState(10);
    const [factorsTotalCount, setFactorsTotalCount] = useState(0);

    // ========== Tab 3: Conflicts ==========
    const [conflicts, setConflicts] = useState([]);
    const [conflictsLoading, setConflictsLoading] = useState(false);
    const [conflictsPage, setConflictsPage] = useState(0);
    const [conflictsRowsPerPage, setConflictsRowsPerPage] = useState(10);
    const [conflictsTotalCount, setConflictsTotalCount] = useState(0);

    // ========== Permissions ==========
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-aae-signal-gen', 'view');
            const add = await hasPermission('tr-aae-signal-gen', 'add');
            setCanView(view);
            setCanAdd(add);
        };
        checkPermissions();
    }, []);

    // ========== Tab 0: Active Signals ==========
    const fetchActiveSignals = useCallback(async () => {
        if (!canView) return;
        setActiveLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: activePage + 1, per_page: activeRowsPerPage, status: 'ACTIVE' });
            const response = await axiosInstance.get(`/api/signal-generation/signals?${params.toString()}`);
            if (response.data.success) {
                setActiveSignals(response.data.data || []);
                setActiveTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch active signals');
        } finally {
            setActiveLoading(false);
        }
    }, [canView, activePage, activeRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchActiveSignals();
    }, [fetchActiveSignals, canView, activeTab]);

    const handleGenerateSignal = async () => {
        setGeneratingSignal(true);
        try {
            await axiosInstance.post('/api/signal-generation/signals/generate');
            fetchActiveSignals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate signal');
        } finally {
            setGeneratingSignal(false);
        }
    };

    const handleGenerateMultiFactor = async () => {
        setGeneratingMultiFactor(true);
        try {
            await axiosInstance.post('/api/signal-generation/signals/multi-factor');
            fetchActiveSignals();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate multi-factor signal');
        } finally {
            setGeneratingMultiFactor(false);
        }
    };

    // ========== Tab 1: Signal History ==========
    const fetchSignalHistory = useCallback(async () => {
        if (!canView) return;
        setHistoryLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: historyPage + 1, per_page: historyRowsPerPage });
            if (historyFilterDirection) params.append('direction', historyFilterDirection);
            if (historyFilterStatus) params.append('status', historyFilterStatus);
            const response = await axiosInstance.get(`/api/signal-generation/signals?${params.toString()}`);
            if (response.data.success) {
                setSignalHistory(response.data.data || []);
                setHistoryTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch signal history');
        } finally {
            setHistoryLoading(false);
        }
    }, [canView, historyPage, historyRowsPerPage, historyFilterDirection, historyFilterStatus]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchSignalHistory();
    }, [fetchSignalHistory, canView, activeTab]);

    // ========== Tab 2: Factor Scores ==========
    const fetchFactorScores = useCallback(async () => {
        if (!canView) return;
        setFactorsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: factorsPage + 1, per_page: factorsRowsPerPage });
            const response = await axiosInstance.get(`/api/signal-generation/factor-scores?${params.toString()}`);
            if (response.data.success) {
                setFactorScores(response.data.data || []);
                setFactorsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch factor scores');
        } finally {
            setFactorsLoading(false);
        }
    }, [canView, factorsPage, factorsRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchFactorScores();
    }, [fetchFactorScores, canView, activeTab]);

    // ========== Tab 3: Conflicts ==========
    const fetchConflicts = useCallback(async () => {
        if (!canView) return;
        setConflictsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: conflictsPage + 1, per_page: conflictsRowsPerPage });
            const response = await axiosInstance.get(`/api/signal-generation/conflicts?${params.toString()}`);
            if (response.data.success) {
                setConflicts(response.data.data || []);
                setConflictsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch conflicts');
        } finally {
            setConflictsLoading(false);
        }
    }, [canView, conflictsPage, conflictsRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchConflicts();
    }, [fetchConflicts, canView, activeTab]);

    // Helper: compute time remaining for signal decay
    const getDecayDisplay = (signal) => {
        if (!signal.expires_at) return '--';
        const now = new Date();
        const expires = new Date(signal.expires_at);
        const diff = expires - now;
        if (diff <= 0) return 'Expired';
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        return `${hours}h ${mins}m`;
    };

    const getDecayPercent = (signal) => {
        if (!signal.expires_at || !signal.created_at) return 0;
        const created = new Date(signal.created_at).getTime();
        const expires = new Date(signal.expires_at).getTime();
        const now = Date.now();
        const total = expires - created;
        const remaining = expires - now;
        if (total <= 0) return 0;
        return Math.max(0, Math.min(100, (remaining / total) * 100));
    };

    // ========== Render Functions ==========
    const renderActiveSignals = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Active Signals
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchActiveSignals} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <>
                            <Button startIcon={generatingSignal ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                variant="contained" onClick={handleGenerateSignal} disabled={generatingSignal} size="small"
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                Generate Signal
                            </Button>
                            <Button startIcon={generatingMultiFactor ? <CircularProgress size={16} /> : <MergeTypeIcon />}
                                variant="outlined" onClick={handleGenerateMultiFactor} disabled={generatingMultiFactor} size="small"
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderColor: ACCENT, color: ACCENT, '&:hover': { borderColor: '#d97706', bgcolor: `${ACCENT}08` } }}>
                                Multi-Factor Signal
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {activeLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Direction</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Strength</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Confidence</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Decay Timer</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Created</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activeSignals.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <SignalCellularAltIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No active signals</Typography>
                                                <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>Generate a signal to get started</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : activeSignals.map((signal) => (
                                        <TableRow key={signal.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {signal.symbol_name || signal.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SignalDirectionLabels[signal.direction] || signal.direction} size="small"
                                                    color={SignalDirectionColors[signal.direction] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SignalStrengthLabels[signal.strength] || signal.strength} size="small"
                                                    color={SignalStrengthColors[signal.strength] || 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={signal.confidence_score != null ? signal.confidence_score * 100 : 0}
                                                        sx={{
                                                            width: 50, height: 5, borderRadius: 3, bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 },
                                                        }}
                                                    />
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.8rem' }}>
                                                        {signal.confidence_score != null ? `${(signal.confidence_score * 100).toFixed(0)}%` : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={`Expires: ${signal.expires_at ? new Date(signal.expires_at).toLocaleString() : 'N/A'}`}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <TimerIcon sx={{ fontSize: 16, color: getDecayPercent(signal) < 25 ? '#ef4444' : '#94a3b8' }} />
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={getDecayPercent(signal)}
                                                            sx={{
                                                                width: 40, height: 4, borderRadius: 2, bgcolor: '#e2e8f0',
                                                                '& .MuiLinearProgress-bar': {
                                                                    bgcolor: getDecayPercent(signal) < 25 ? '#ef4444' : getDecayPercent(signal) < 50 ? ACCENT : '#10b981',
                                                                    borderRadius: 2,
                                                                },
                                                            }}
                                                        />
                                                        <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b' }}>
                                                            {getDecayDisplay(signal)}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {signal.created_at ? new Date(signal.created_at).toLocaleString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={activeTotalCount} page={activePage}
                            onPageChange={(e, p) => setActivePage(p)} rowsPerPage={activeRowsPerPage}
                            onRowsPerPageChange={(e) => { setActiveRowsPerPage(parseInt(e.target.value, 10)); setActivePage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderSignalHistory = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Signal History
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontFamily }}>Direction</InputLabel>
                        <Select value={historyFilterDirection} onChange={(e) => { setHistoryFilterDirection(e.target.value); setHistoryPage(0); }} label="Direction" sx={{ fontFamily }}>
                            <MenuItem value="">All</MenuItem>
                            {Object.entries(SignalDirection).map(([key, value]) => (
                                <MenuItem key={key} value={value}>{SignalDirectionLabels[key]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ fontFamily }}>Status</InputLabel>
                        <Select value={historyFilterStatus} onChange={(e) => { setHistoryFilterStatus(e.target.value); setHistoryPage(0); }} label="Status" sx={{ fontFamily }}>
                            <MenuItem value="">All</MenuItem>
                            {Object.entries(AAESignalStatus).map(([key, value]) => (
                                <MenuItem key={key} value={value}>{AAESignalStatusLabels[key]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchSignalHistory} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {historyLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Direction</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Strength</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Confidence</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>PnL Impact</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Created</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Expired</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {signalHistory.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No signals found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : signalHistory.map((signal) => (
                                        <TableRow key={signal.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {signal.symbol_name || signal.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SignalDirectionLabels[signal.direction] || signal.direction} size="small"
                                                    color={SignalDirectionColors[signal.direction] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SignalStrengthLabels[signal.strength] || signal.strength} size="small"
                                                    color={SignalStrengthColors[signal.strength] || 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {signal.confidence_score != null ? `${(signal.confidence_score * 100).toFixed(0)}%` : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={AAESignalStatusLabels[signal.status] || signal.status} size="small"
                                                    color={AAESignalStatusColors[signal.status] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{
                                                fontFamily, fontWeight: 600, fontSize: '0.82rem',
                                                color: signal.pnl_impact > 0 ? '#10b981' : signal.pnl_impact < 0 ? '#ef4444' : '#64748b',
                                            }}>
                                                {signal.pnl_impact != null ? `${signal.pnl_impact > 0 ? '+' : ''}${(signal.pnl_impact * 100).toFixed(2)}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {signal.created_at ? new Date(signal.created_at).toLocaleDateString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {signal.expires_at ? new Date(signal.expires_at).toLocaleDateString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={historyTotalCount} page={historyPage}
                            onPageChange={(e, p) => setHistoryPage(p)} rowsPerPage={historyRowsPerPage}
                            onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value, 10)); setHistoryPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderFactorScores = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Factor Scores
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={fetchFactorScores} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {factorsLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Factor Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Score</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Weight</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Weighted Score</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Confidence</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {factorScores.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No factor scores found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : factorScores.map((factor) => (
                                        <TableRow key={factor.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {factor.symbol_name || factor.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={FactorTypeLabels[factor.factor_type] || factor.factor_type} size="small"
                                                    color={FactorTypeColors[factor.factor_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={factor.score != null ? Math.abs(factor.score) * 100 : 0}
                                                        sx={{
                                                            width: 50, height: 5, borderRadius: 3, bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: factor.score > 0 ? '#10b981' : '#ef4444',
                                                                borderRadius: 3,
                                                            },
                                                        }}
                                                    />
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: factor.score > 0 ? '#10b981' : factor.score < 0 ? '#ef4444' : '#64748b', fontSize: '0.8rem' }}>
                                                        {factor.score != null ? factor.score.toFixed(4) : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {factor.weight != null ? `${(factor.weight * 100).toFixed(1)}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {factor.weighted_score != null ? factor.weighted_score.toFixed(4) : (factor.score != null && factor.weight != null ? (factor.score * factor.weight).toFixed(4) : '--')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {factor.confidence != null ? `${(factor.confidence * 100).toFixed(0)}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {factor.date ? new Date(factor.date).toLocaleDateString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={factorsTotalCount} page={factorsPage}
                            onPageChange={(e, p) => setFactorsPage(p)} rowsPerPage={factorsRowsPerPage}
                            onRowsPerPageChange={(e) => { setFactorsRowsPerPage(parseInt(e.target.value, 10)); setFactorsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderConflicts = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Signal Conflict Resolution
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={fetchConflicts} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {conflictsLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Conflicting Signals</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Resolution</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Resolved Direction</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Confidence</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Reason</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {conflicts.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No conflicts found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : conflicts.map((conflict) => (
                                        <TableRow key={conflict.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {conflict.symbol_name || conflict.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {(conflict.conflicting_directions || []).map((dir, i) => (
                                                        <Chip key={i} label={SignalDirectionLabels[dir] || dir} size="small"
                                                            color={SignalDirectionColors[dir] || 'default'}
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                                    ))}
                                                    {(!conflict.conflicting_directions || conflict.conflicting_directions.length === 0) && (
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                            {conflict.conflict_description || '--'}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={conflict.resolution_method || 'Auto'} size="small" color="info" variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SignalDirectionLabels[conflict.resolved_direction] || conflict.resolved_direction || '--'} size="small"
                                                    color={SignalDirectionColors[conflict.resolved_direction] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {conflict.resolved_confidence != null ? `${(conflict.resolved_confidence * 100).toFixed(0)}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={conflict.reason || ''}>
                                                    <span>{conflict.reason || '--'}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {conflict.created_at ? new Date(conflict.created_at).toLocaleDateString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={conflictsTotalCount} page={conflictsPage}
                            onPageChange={(e, p) => setConflictsPage(p)} rowsPerPage={conflictsRowsPerPage}
                            onRowsPerPageChange={(e) => { setConflictsRowsPerPage(parseInt(e.target.value, 10)); setConflictsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ========== Permission Guard ==========
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Signal Generation.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SignalCellularAltIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Signal Generation
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Multi-factor signal generation, factor scoring, and conflict resolution
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                    <Tab label="Active Signals" icon={<SignalCellularAltIcon />} iconPosition="start" />
                    <Tab label="Signal History" icon={<HistoryIcon />} iconPosition="start" />
                    <Tab label="Factor Scores" icon={<ScoreIcon />} iconPosition="start" />
                    <Tab label="Conflicts" icon={<GavelIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderActiveSignals()}
            {activeTab === 1 && renderSignalHistory()}
            {activeTab === 2 && renderFactorScores()}
            {activeTab === 3 && renderConflicts()}
        </Box>
    );
};

export default SignalGenerationPage;
